import { Building2, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { BrandLockup } from "../brand/BrandMark";
import { LegalLine } from "../legal/LegalFooter";
import { EmptyState, ErrorPanel } from "../ui/components";
import { BoundarySkeleton } from "../ui/skeletons";
import { useAuth } from "./AuthProvider";
import { authorityLabel } from "./permissions";

export function TenantSelectPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>();
  const [working, setWorking] = useState("");

  if (auth.loading) return <BoundarySkeleton />;
  if (!auth.session) return <Navigate to="/login" replace />;
  if (auth.session.memberships.length === 0 && auth.session.user.platformRole !== "none")
    return <Navigate to="/operator/overview" replace />;

  async function choose(tenantId: string) {
    setWorking(tenantId);
    setError(undefined);
    try {
      await auth.selectTenant(tenantId);
      navigate("/", { replace: true });
    } catch (caught) {
      setError(caught);
      setWorking("");
    }
  }

  return (
    <main className="auth-layout">
      <section className="auth-brand">
        <BrandLockup />
        <div>
          <h1>Choose your workspace.</h1>
          <p>You’ll only see the venues and tools available to your account.</p>
        </div>
        <footer>Your access is checked whenever you open a workspace.</footer>
      </section>
      <section className="auth-panel" aria-labelledby="tenant-title">
        <div className="auth-card">
          <h1 id="tenant-title">Organizations</h1>
          <p>Signed in as {auth.session.user.email}</p>
          {error ? <ErrorPanel error={error} /> : null}
          {auth.session.memberships.length === 0 ? (
            <EmptyState
              title="No staff workspaces"
              description="This account is for a customer portal. Open the link supplied by the venue team, or ask an organization owner to invite you as staff."
            />
          ) : (
            <div className="tenant-list">
              {auth.session.memberships.map((membership) => (
                <button
                  className="tenant-choice"
                  type="button"
                  key={membership.id}
                  disabled={working !== ""}
                  onClick={() => choose(membership.tenantId)}
                >
                  <span className="inline">
                    <Building2 aria-hidden="true" />
                    <span>
                      <strong>{membership.tenantName}</strong>
                      <small>
                        {authorityLabel(membership.eventRole, membership.bookingRole)} ·{" "}
                        {membership.allVenues
                          ? "All venues"
                          : `${membership.venueIds?.length ?? 0} venues`}
                      </small>
                    </span>
                  </span>
                  {working === membership.tenantId ? (
                    <span className="spinner" role="status" aria-label="Selecting" />
                  ) : (
                    <ChevronRight aria-hidden="true" />
                  )}
                </button>
              ))}
            </div>
          )}
          <button className="button secondary" type="button" onClick={() => auth.signOut()}>
            Sign out
          </button>
        </div>
        <LegalLine />
      </section>
    </main>
  );
}
